import {
  ExamImportError,
  ExamImportRequest,
  ExamPartImportRequest,
  ExamPartQuestionImportRequest,
  ExamTemplateRequest,
} from '../../model/request';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { QuestionType } from 'common/enum';
import { processErrorFromExamImport } from '../../utils/utils';
import { Subject } from '../../model/response';

interface ExamEditState {
  examRequest: ExamImportRequest | null;
  examTemplate: ExamTemplateRequest | null;
  subject: Subject | null;
  examImportError: ExamImportError | null;
  practice: boolean;
}

const initialState: ExamEditState = {
  examRequest: null,
  examTemplate: null,
  subject: null,
  examImportError: null,
  practice: false,
};

const examEditSlice = createSlice({
  name: 'examEdit',
  initialState,
  reducers: {
    initExamEdit: (
      state,
      action: PayloadAction<{
        examImportRequest: ExamImportRequest;
        examTemplate: ExamTemplateRequest | null;
        subject: Subject | null;
        practice: boolean;
      }>,
    ) => {
      state.examRequest = action.payload.examImportRequest;
      state.examTemplate = action.payload.examTemplate;
      state.subject = action.payload.subject;
      state.examImportError = processErrorFromExamImport(
        action.payload.examImportRequest,
        action.payload.examTemplate,
        action.payload.subject,
        action.payload.practice,
      );
      state.practice = action.payload.practice;
    },
    updateExamRequest: (state, action: PayloadAction<ExamImportRequest>) => {
      if (!state.examRequest) {
        return;
      }
      state.examRequest = action.payload;
      state.examImportError = processErrorFromExamImport(
        state.examRequest,
        state.examTemplate,
        state.subject,
        state.practice,
      );
    },
    updateExamPartRequest: (state, action: PayloadAction<ExamPartImportRequest>) => {
      if (!state.examRequest) {
        return;
      }
      state.examRequest.examParts = state.examRequest.examParts.map((examPart) => {
        if (examPart.id !== action.payload.id) {
          return examPart;
        }
        return {
          ...action.payload,
        };
      });
      state.examImportError = processErrorFromExamImport(
        state.examRequest,
        state.examTemplate,
        state.subject,
        state.practice,
      );
    },
    updateExamPartRequests: (state, action: PayloadAction<ExamPartImportRequest[]>) => {
      if (!state.examRequest) {
        return;
      }
      state.examRequest.examParts = action.payload;
      state.examImportError = processErrorFromExamImport(
        state.examRequest,
        state.examTemplate,
        state.subject,
        state.practice,
      );
    },
    updateExamPartQuestionRequest: (state, action: PayloadAction<ExamPartQuestionImportRequest>) => {
      if (!state.examRequest) {
        return;
      }
      const updateFields = (currentState: any, actionUpdate: PayloadAction<ExamPartQuestionImportRequest>) => {
        currentState.mark = actionUpdate.payload.mark;
        currentState.weight = actionUpdate.payload.weight;
        currentState.question.id = actionUpdate.payload.question.id;
        currentState.question.name = actionUpdate.payload.question.name;
        currentState.question.code = actionUpdate.payload.question.code;
        currentState.question.topic = actionUpdate.payload.question.topic;
        currentState.question.type = actionUpdate.payload.question.type;
        currentState.question.pageNumber = actionUpdate.payload.question.pageNumber;
        currentState.question.thinkingLevels = actionUpdate.payload.question.thinkingLevels;
        currentState.question.hint = actionUpdate.payload.question.hint;
        currentState.question.solutionGuide = actionUpdate.payload.question.solutionGuide;
        currentState.question.image = actionUpdate.payload.question.image;
        currentState.question.imageSize = actionUpdate.payload.question.imageSize;
        currentState.examPartQuestionIndex = actionUpdate.payload.examPartQuestionIndex;
        currentState.question.rightAnswer = actionUpdate.payload.question.rightAnswer;
        currentState.question.bookAppendix = actionUpdate.payload.question.bookAppendix;
        currentState.question.skillTypes = actionUpdate.payload.question.skillTypes;
        currentState.question.parent = actionUpdate.payload.question.parent;
        currentState.question.exerciseFormats = actionUpdate.payload.question.exerciseFormats;
        currentState.question.questionDetails = actionUpdate.payload.question.questionDetails;
      };

      const updateGroupQuestionFields = (
        currentState: any,
        actionUpdate: PayloadAction<ExamPartQuestionImportRequest>,
      ) => {
        currentState.question.id = actionUpdate.payload.question.id;
        currentState.question.name = actionUpdate.payload.question.name;
        currentState.question.code = actionUpdate.payload.question.code;
        currentState.question.topic = actionUpdate.payload.question.topic;
        currentState.question.type = actionUpdate.payload.question.type;
        currentState.question.solutionGuide = actionUpdate.payload.question.solutionGuide;
        currentState.examPartQuestionIndex = actionUpdate.payload.examPartQuestionIndex;
        currentState.question.parent = actionUpdate.payload.question.parent;
      };

      state.examRequest.examParts.forEach((examPart) => {
        examPart.examPartQuestions.forEach((examPartQuestion) => {
          if (
            action.payload.question.type !== QuestionType.GROUPED_QUESTION &&
            examPartQuestion.id === action.payload.id
          ) {
            updateFields(examPartQuestion, action);
          } else if (
            action.payload.question.type === QuestionType.GROUPED_QUESTION &&
            examPartQuestion.id === action.payload.id
          ) {
            updateGroupQuestionFields(examPartQuestion, action);
          } else if (examPartQuestion.children && examPartQuestion.children.length > 0) {
            examPartQuestion.children.forEach((childExamPartQuestion) => {
              if (childExamPartQuestion.id === action.payload.id) {
                updateFields(childExamPartQuestion, action);
              }
            });
          }
        });
      });

      state.examImportError = processErrorFromExamImport(
        state.examRequest,
        state.examTemplate,
        state.subject,
        state.practice,
      );
    },
    updateExamImportError: (state) => {
      if (!state.examRequest) {
        return;
      }
      state.examImportError = processErrorFromExamImport(
        state.examRequest,
        state.examTemplate,
        state.subject,
        state.practice,
      );
    },
  },
});

export const {
  initExamEdit,
  updateExamRequest,
  updateExamPartRequest,
  updateExamPartQuestionRequest,
  updateExamPartRequests,
  updateExamImportError,
} = examEditSlice.actions;

export default examEditSlice;
